import { writePortalPbCustomerJson } from '../../constants';
import type { CustomerRecord } from '../CustomerLogin';
import { readSeatElementEnabled, readSeatMatrixUserId } from './elementSeatInvite';
import { isHonestMatrixRoomId } from './portalMatrixOrigins';

export type PortalRole = 'it_admin' | 'member' | 'praktikant';
export type AccountKind = 'beta_tester' | 'demo_prospect' | 'customer';

export function parseAccountKind(raw: unknown): AccountKind {
  if (raw === 'beta_tester' || raw === 'demo_prospect' || raw === 'customer') {
    return raw;
  }
  return 'customer';
}

export function isBetaTester(accountKind?: AccountKind | null): boolean {
  return accountKind === 'beta_tester';
}

/** Kurzlebige Session — nur für Pflicht-Passwortwechsel direkt nach Login. */
export const PORTAL_TEMP_LOGIN_PW_KEY = 'portal_temp_login_pw';

export function buildCustomerRecord(record: Record<string, unknown>): CustomerRecord {
  const id = typeof record.id === 'string' ? record.id : '';
  const email = typeof record.email === 'string' ? record.email : '';
  if (!id || !email) {
    throw new Error('Ungültige Kunden-Antwort vom Server.');
  }
  const tierRaw = record.tier;
  const rawRoom =
    (typeof record.matrixRoomId === 'string' && record.matrixRoomId) ||
    (typeof record.matrix_room_id === 'string' && record.matrix_room_id) ||
    '';
  const matrixRoomId = isHonestMatrixRoomId(rawRoom) ? String(rawRoom).trim() : '';
  return {
    id,
    email,
    name: typeof record.name === 'string' ? record.name : '',
    kanzlei: typeof record.kanzlei === 'string' ? record.kanzlei : '',
    kanzlei_id:
      typeof record.kanzlei_id === 'string' && record.kanzlei_id.trim()
        ? record.kanzlei_id.trim().replace(/[^a-zA-Z0-9_-]/g, '_').substring(0, 80)
        : undefined,
    tier: tierRaw === 'premium' ? 'premium' : 'standard',
    matrixRoomId,
    matrixUserId: readSeatMatrixUserId(record),
    elementSeat: readSeatElementEnabled(record),
    status: typeof record.status === 'string' ? record.status : '',
    must_change_password: record.must_change_password === true,
    portal_role:
      record.portal_role === 'it_admin'
        ? 'it_admin'
        : record.portal_role === 'praktikant'
          ? 'praktikant'
          : record.portal_role === 'member'
            ? 'member'
            : undefined,
    is_primary: record.is_primary === true,
    account_kind: parseAccountKind(record.account_kind),
    included_seats:
      typeof record.included_seats === 'number' && Number.isFinite(record.included_seats)
        ? record.included_seats
        : undefined,
    max_seats:
      typeof record.max_seats === 'number' && Number.isFinite(record.max_seats)
        ? record.max_seats
        : undefined,
    baustein_kategorien: readBausteinKategorienField(record.baustein_kategorien),
    module_judicature: record.module_judicature === true,
  };
}

/** Rohfeld am Sitz. Kein Tarif, keine Hilfsfunktion aus einem anderen Branch. */
function readBausteinKategorienField(raw: unknown): CustomerRecord['baustein_kategorien'] {
  if (typeof raw === 'string') return raw;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string') out[key] = value;
  }
  return out;
}

/**
 * Orbis Professional (`standard`) und Premium — IT-Admin oder primärer Erst-Account.
 * Litigation bleibt premium-only; Sitze-UI gilt für beide Standard-Tarife.
 */
export function canManageTeamAccess(
  customer: Pick<CustomerRecord, 'tier' | 'portal_role' | 'is_primary'>,
): boolean {
  if (customer.tier !== 'premium' && customer.tier !== 'standard') return false;
  if (customer.portal_role === 'member' || customer.portal_role === 'praktikant') return false;
  return customer.portal_role === 'it_admin' || customer.is_primary === true;
}

/** Akte schreiben (Upload, OO-Save, Löschen/Archiv). Praktikant / Leih nur lesen. */
export function canWritePortalDocuments(
  customer: Pick<CustomerRecord, 'portal_role'>,
): boolean {
  return customer.portal_role !== 'praktikant';
}

/** IT-Admin/Primary der Kanzlei oder Aktenführer — Mitglieder der Akte zuweisen. Praktikant nie. */
export function canManageAkteMembers(
  actor: Pick<CustomerRecord, 'id' | 'portal_role' | 'is_primary'>,
  akte: { aktenfuehrer_id?: string },
): boolean {
  if (actor.portal_role === 'praktikant') return false;
  if (actor.portal_role === 'it_admin' || actor.is_primary === true) return true;
  return Boolean(akte.aktenfuehrer_id && akte.aktenfuehrer_id === actor.id);
}

/**
 * IT-Admin/Primary sieht Kanzlei-Akten auch ohne Mitgliedschaft — UI muss das als Überblick kennzeichnen.
 * Ohne geladene Mitgliedschaften kein Label (kein Fehl-Überblick auf eigenen Akten).
 */
export function isItAdminAkteOverview(
  actor: Pick<CustomerRecord, 'id' | 'portal_role' | 'is_primary'>,
  akte: { id: string },
  memberAkteIds: ReadonlySet<string> | null,
): boolean {
  if (memberAkteIds == null) return false;
  if (actor.portal_role !== 'it_admin' && actor.is_primary !== true) return false;
  return !memberAkteIds.has(akte.id);
}

/**
 * Sichtbarkeit: nur Akten mit Mitgliedschaft. IT-Admin/Primary behält die PB-Liste
 * (Überblick). Alle anderen bleiben bis zur geladenen Mitgliedschaftsliste leer
 * statt kurzzeitig die kanzleiweite PB-Liste zu sehen (fail-closed).
 */
export function filterPortalAktenForActor<T extends { id: string }>(
  akten: T[],
  actor: Pick<CustomerRecord, 'portal_role' | 'is_primary'>,
  memberAkteIds: ReadonlySet<string> | null,
): T[] {
  if (actor.portal_role === 'it_admin' || actor.is_primary === true) return akten;
  if (memberAkteIds == null) return [];
  return akten.filter((row) => memberAkteIds.has(row.id));
}

/** Praktikant bleibt lesen, auch wenn der Client write schickt (Hook-Spiegel). */
export function coercePortalAkteMemberAccess(input: {
  access: 'read' | 'write' | string;
  targetRole?: string;
  isAktenfuehrer?: boolean;
}): 'read' | 'write' {
  if (input.isAktenfuehrer) return 'write';
  if (String(input.targetRole || '').trim().toLowerCase() === 'praktikant') return 'read';
  return input.access === 'write' ? 'write' : 'read';
}

export function persistPortalCustomer(customer: CustomerRecord): void {
  writePortalPbCustomerJson(JSON.stringify(customer));
}
